import { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, Loader2, Truck, CheckCircle2, Clock, Package, Banknote, CreditCard, AlertTriangle, MapPin } from "lucide-react";
import { Select, DatePicker, Modal, DriverPaymentModal } from "../../../components";
import axios from "axios";
import { GET_DRIVER_ROUTES_URL, GET_DRIVER_ALL_ROUTES_URL, GET_DRIVER_ORDERS_URL } from "../../../features/UrlHelper";
import { compareMenuCodesNatural, getOrderMapsLink } from "../../../features/orderUtils";
import { useOrdersRealtime, matchesOrderScope } from "../../../context/RealtimeContext";
import "./DriverRoutesSection.scss";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatCurrency(amount) {
  return Number(amount || 0).toFixed(2).replace(".", ",") + " €";
}

// Agreguje jedlá DELIVERY objednávok do poľa {code, qty, name}
function isNotDeliveredCancelled(o) {
  return o.status === "CANCELLED" && o.payment_type === "NOT_DELIVERED";
}

function buildCargoItems(orders) {
  const deliveryOrders = orders.filter(
    (o) => o.status === "DELIVERY" || isNotDeliveredCancelled(o)
  );
  if (deliveryOrders.length === 0) return [];

  const counts = {};
  const names = {};
  for (const order of deliveryOrders) {
    for (const item of order.items || []) {
      const key = item.menu_code || item.name;
      counts[key] = (counts[key] || 0) + (item.quantity || 1);
      if (!names[key]) names[key] = item.name || key;
    }
  }

  return Object.entries(counts)
    .sort(([a], [b]) => compareMenuCodesNatural(a, b))
    .map(([code, qty]) => ({ code, qty, name: names[code] }));
}

// ─── StatCard ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, className, onClick }) {
  return (
    <div
      className={["drs__stat-card", className].filter(Boolean).join(" ")}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
    >
      <div className="drs__stat-body">
        <span className="drs__stat-label">{label}</span>
        <span className="drs__stat-value">{value}</span>
      </div>
    </div>
  );
}

// ─── DriverOrderCard ──────────────────────────────────────────────────────────
const STATUS_LABELS = {
  DELIVERY:  "Na trase",
  ACCEPTED:  "Prijatá",
  DELIVERED: "Doručená",
  CANCELLED: "Zrušená",
};

// Farby stavov ako CSS premenné (styles/theme.scss), aby sedeli so svetlým
// aj tmavým režimom. Idú do --status-color odznaku.
const STATUS_BADGE_COLORS = {
  ACCEPTED:  "var(--c-stav-prijata)",
  DELIVERY:  "var(--c-stav-na-trase)",
  DELIVERED: "var(--c-success)",
  CANCELLED: "var(--c-error)",
};

const STATUS_ORDER = { DELIVERY: 0, ACCEPTED: 1, DELIVERED: 2 };

function sortOrders(list) {
  return [...list].sort(
    (a, b) => (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99)
  );
}

function DriverOrderCard({ order, onDeliveryClick, isOwnRoute }) {
  const [itemsModalOpen,    setItemsModalOpen]    = useState(false);
  const [isEditWindowOpen,  setIsEditWindowOpen]  = useState(false);

  const isOnRoute      = order.status === "DELIVERY" && isOwnRoute;
  const isDelivered    = order.status === "DELIVERED";
  const isNotDelivered = order.status === "CANCELLED" && order.payment_type === "NOT_DELIVERED";

  useEffect(() => {
    if (order.status !== "DELIVERED" || !order.delivered_at) {
      setIsEditWindowOpen(false);
      return;
    }

    const checkWindow = () => {
      const deliveredTime = new Date(order.delivered_at).getTime();
      const currentTime = new Date().getTime();
      const diffInMinutes = (currentTime - deliveredTime) / (1000 * 60);
      setIsEditWindowOpen(diffInMinutes < 5);
    };

    checkWindow();
    const interval = setInterval(checkWindow, 15000);
    return () => clearInterval(interval);
  }, [order.status, order.delivered_at]);

  const { url: mapsUrl, hasPreciseLocation } = getOrderMapsLink(order);

  const items = (order.items || []).filter((i) => (i.quantity || 1) > 0);
  const isDebtPayment = items.length === 0 || Number(order.total_price || 0) === 0;

  const saldo = Number(order.customer_saldo || 0);
  const saldoText =
    saldo < 0
      ? `${formatCurrency(Math.abs(saldo))} (Dlh)`
      : saldo > 0
      ? `${formatCurrency(saldo)} (Preplatok)`
      : "—";

  const saldoValueClass = [
    "drs-order-card__saldo-value",
    isDebtPayment
      ? "drs-order-card__saldo-value--highlighted"
      : saldo < 0
      ? "drs-order-card__saldo-value--debt"
      : saldo > 0
      ? "drs-order-card__saldo-value--credit"
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  const cardClass = [
    "drs-order-card",
    isDelivered    ? "drs-order-card--delivered"     : "",
    isOnRoute      ? "drs-order-card--on-route"      : "",
    isNotDelivered ? "drs-order-card--not-delivered" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const showDeliverBar = order.status === "DELIVERY" && isOwnRoute;
  const showEditBar    = isEditWindowOpen && isOwnRoute;

  return (
    <div
      className={cardClass}
      onClick={isOnRoute ? onDeliveryClick : undefined}
      role={isOnRoute ? "button" : undefined}
      tabIndex={isOnRoute ? 0 : undefined}
      onKeyDown={isOnRoute ? (e) => e.key === "Enter" && onDeliveryClick?.() : undefined}
    >
      {/* ── Header ── */}
      <div className="drs-order-card__header">
        <div className="drs-order-card__header-left">
          {order.address_code && (
            <span className="drs-order-card__code">{order.address_code}</span>
          )}
          <a
            className="drs-order-card__address"
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {order.delivery_address}
            {hasPreciseLocation && (
              <MapPin size={13} className="drs-order-card__pin" title="Spresnená poloha" />
            )}
          </a>
        </div>
        <span
          className="drs-order-card__status-badge"
          style={{ "--status-color": STATUS_BADGE_COLORS[order.status] || "var(--c-stav-neznamy)" }}
        >
          {STATUS_LABELS[order.status]}
        </span>
      </div>

      {/* ── Body ── */}
      <div className="drs-order-card__body">
        <div className="drs-order-card__customer-row">
          <span className="drs-order-card__customer-name">
            {order.customer_name} {order.customer_surname}
          </span>
          {order.customer_phone && (
            <a
              className="drs-order-card__phone"
              href={`tel:${order.customer_phone}`}
              onClick={(e) => e.stopPropagation()}
            >
              {order.customer_phone}
            </a>
          )}
        </div>

        {order.customer_note && (
          <div className="drs-order-card__customer-note">
            {order.customer_note}
          </div>
        )}

        {items.length === 0 ? (
          <span className="drs-order-card__debt-payment">Vyplatenie dlhu</span>
        ) : (
          <div
            className="drs-order-card__items drs-order-card__items--clickable"
            onClick={(e) => { e.stopPropagation(); setItemsModalOpen(true); }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") setItemsModalOpen(true); }}
            title="Zobraziť detail položiek"
          >
            {items.map((item, idx) => (
              <span key={idx} className="drs-order-card__item-chip">
                {item.quantity || 1}x{" "}
                <strong className="drs-order-card__item-code" title={item.name}>
                  {item.menu_code || item.name}
                </strong>
              </span>
            ))}
          </div>
        )}

        <div className="drs-order-card__footer">
          <span className="drs-order-card__price">
            Cena obeda: <strong className={`drs-order-card__price-value${!isDebtPayment ? " drs-order-card__price-value--highlighted" : ""}`}>{formatCurrency(order.total_price)}</strong>
          </span>
          <span className="drs-order-card__saldo">
            Saldo: <strong className={saldoValueClass}>{saldoText}</strong>
          </span>
        </div>

        {order.dispatcher_note && (
          <div className="drs-order-card__note">📋 {order.dispatcher_note}</div>
        )}

        {isNotDelivered && order.driver_note && (
          <div className="drs-order-card__not-delivered-note">
            Dôvod nedoručenia: {order.driver_note}
          </div>
        )}
      </div>

      {/* ── Doručiť bar (iba DELIVERY + vlastná trasa) ── */}
      {showDeliverBar && (
        <button
          className="drs-order-card__deliver-bar"
          onClick={(e) => { e.stopPropagation(); onDeliveryClick?.(); }}
          type="button"
        >
          <CreditCard size={16} />
          <span>Doručiť a zaplatiť</span>
        </button>
      )}

      {/* ── Upraviť platbu bar (iba DELIVERED v 5-min. okne + vlastná trasa) ── */}
      {showEditBar && (
        <button
          className="drs-order-card__edit-bar"
          onClick={(e) => { e.stopPropagation(); onDeliveryClick?.(); }}
          type="button"
        >
          <CreditCard size={16} />
          <span>Upraviť platbu</span>
        </button>
      )}

      <Modal
        isOpen={itemsModalOpen}
        onClose={() => setItemsModalOpen(false)}
        title="Položky objednávky"
      >
        <div className="order-items-modal">
          {items.map((item, idx) => (
            <div key={idx} className="order-items-modal__row">
              <span className="order-items-modal__qty">{item.quantity || 1}x</span>
              {item.menu_code && (
                <strong className="order-items-modal__code">{item.menu_code}</strong>
              )}
              <span className="order-items-modal__name">{item.name}</span>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}

// ─── DriverRoutesSection ──────────────────────────────────────────────────────
function DriverRoutesSection() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [ownRouteIds, setOwnRouteIds]   = useState(new Set());
  const [allRoutes, setAllRoutes]       = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [cargoModalOpen, setCargoModalOpen] = useState(false);
  const [paymentModalOrder, setPaymentModalOrder] = useState(null);
  const ordersSeqRef = useRef(0);
  const routesSeqRef = useRef(0);

  const dateStr = toISODate(selectedDate);

  const isOwnRoute = selectedRouteId ? ownRouteIds.has(selectedRouteId) : false;

  // Načítanie trás vodiča + všetkých trás firmy
  const fetchRoutes = useCallback(async ({ silent = false } = {}) => {
    const seq = ++routesSeqRef.current;
    if (!silent) setLoadingRoutes(true);
    try {
      const [ownRes, allRes] = await Promise.all([
        axios.get(GET_DRIVER_ROUTES_URL),
        axios.get(GET_DRIVER_ALL_ROUTES_URL),
      ]);
      if (seq !== routesSeqRef.current) return;
      const ownIds = new Set(ownRes.data.map((r) => String(r.id)));
      setOwnRouteIds(ownIds);
      setAllRoutes(allRes.data);
      // Auto-select prvú vlastnú trasu, ak existuje. Pri tichom refreshi sa
      // voľba vodiča nepreberá -- prepnúť mu trasu pod rukami by bolo horšie
      // než nechať ho na tej, ktorú si vybral.
      if (silent) return;
      if (ownRes.data.length > 0) {
        setSelectedRouteId(String(ownRes.data[0].id));
      } else if (allRes.data.length > 0) {
        setSelectedRouteId(String(allRes.data[0].id));
      }
    } catch {
      if (!silent) setAllRoutes([]);
    } finally {
      if (!silent && seq === routesSeqRef.current) setLoadingRoutes(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Načítanie objednávok
  const fetchOrders = useCallback(async ({ silent = false } = {}) => {
    if (!selectedRouteId) return;
    const seq = ++ordersSeqRef.current;
    if (!silent) setLoadingOrders(true);
    try {
      const { data } = await axios.get(
        GET_DRIVER_ORDERS_URL(dateStr, selectedRouteId)
      );
      if (seq !== ordersSeqRef.current) return;
      setOrders(data);
    } catch {
      // Pri refreshi na pozadí sa zoznam NEnuluje. Vodič je v teréne na
      // kolísavej sieti a vyprázdnená obrazovka uprostred rozvozu je
      // najhoršie, čo sa mu môže stať.
      if (!silent) setOrders([]);
    } finally {
      if (!silent && seq === ordersSeqRef.current) setLoadingOrders(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr, selectedRouteId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // ─── Realtime auto-refresh ──────────────────────────────────────────────────
  const paymentModalOpenRef = useRef(false);
  paymentModalOpenRef.current = !!paymentModalOrder;
  const pendingRefetchRef = useRef(false);

  const refetchOrders = useCallback(() => {
    if (paymentModalOpenRef.current) {
      pendingRefetchRef.current = true;
      return;
    }
    fetchOrders({ silent: true });
  }, [fetchOrders]);

  useEffect(() => {
    if (!paymentModalOrder && pendingRefetchRef.current) {
      pendingRefetchRef.current = false;
      fetchOrders({ silent: true });
    }
  }, [paymentModalOrder, fetchOrders]);

  useOrdersRealtime({
    filter: (evt) => matchesOrderScope(evt, { date: dateStr, routeId: selectedRouteId }),
    onChange: refetchOrders,
  });

  // Zmena trasy môže vodičovi trasu pridať alebo odobrať (default_driver_id),
  // čím sa mení isOwnRoute -- a teda aj to, či sa mu vôbec vykreslia tlačidlá
  // doručenia.
  useOrdersRealtime({
    filter: (evt) => evt.entity === "route" || evt.entity === "resync",
    onChange: () => fetchRoutes({ silent: true }),
    debounceMs: 1000,
  });

  // Bezpečnostný poll iba na tejto obrazovke. Mobilné siete a suspendovanie
  // aplikácie robia "SSE je ticho mŕtve" pravdepodobnejším než kdekoľvek inde;
  // jeden request za 90 s nestojí nič a pokrýva vodiča, ktorý nevidí nové
  // objednávky.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") refetchOrders();
    }, 90000);
    return () => clearInterval(id);
  }, [refetchOrders]);

  // ── Výpočty štatistík ──────────────────────────────────────────────────────
  const allCount        = orders.filter((o) =>
    ["ACCEPTED", "DELIVERY", "DELIVERED"].includes(o.status) || isNotDeliveredCancelled(o)
  ).length;
  const inDeliveryCount = orders.filter((o) => o.status === "DELIVERY").length;
  const deliveredCount  = orders.filter(
    (o) => o.status === "DELIVERED" || isNotDeliveredCancelled(o)
  ).length;
  const cargoItems      = buildCargoItems(orders);
  const cargoValue = cargoItems.length === 0 ? "—" : (
    <span className="drs__cargo-chips">
      {cargoItems.map(({ code, qty }) => (
        <span key={code} className="drs__cargo-chip">
          {qty}x <strong className="order-items-modal__code">{code}</strong>
        </span>
      ))}
    </span>
  );
  const totalCash     = orders
    .filter((o) => !isNotDeliveredCancelled(o))
    .reduce((sum, o) => sum + Number(o.total_price || 0), 0);
  const collectedCash = orders
    .filter((o) => o.status === "DELIVERED")
    .reduce((sum, o) => sum + Number(o.total_price || 0), 0);

  // ── Možnosti pre Select ────────────────────────────────────────────────────
  const ownRoutes   = allRoutes.filter((r) => ownRouteIds.has(String(r.id)));
  const otherRoutes = allRoutes.filter((r) => !ownRouteIds.has(String(r.id)));

  const buildRouteOption = (r, isOther) => ({
    value: String(r.id),
    label: (
      <span className="drs__route-option">
        <span className="drs__route-dot" style={{ background: r.color_code }} />
        {r.name}
        {isOther && <span className="drs__route-option-badge">Iná trasa</span>}
      </span>
    ),
  });

  const routeOptions = [
    ...ownRoutes.map((r) => buildRouteOption(r, false)),
    ...(otherRoutes.length > 0
      ? [
          { value: "__divider__", label: <span className="drs__route-divider">Ostatné trasy</span>, disabled: true },
          ...otherRoutes.map((r) => buildRouteOption(r, true)),
        ]
      : []),
  ];

  return (
    <div className="drs">
      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="drs__header">
        <h1 className="drs__title">Moje závozy</h1>
      </div>

      {/* ── Filter panel ──────────────────────────────────────────────────── */}
      <div className="dm__filter drs__filter">
        <div className="drs__controls-row">
          <div className="drs__date-nav">
            <button
              className="dm__week-nav"
              onClick={() =>
                setSelectedDate((d) => {
                  const n = new Date(d);
                  n.setDate(n.getDate() - 1);
                  return n;
                })
              }
              title="Predchádzajúci deň"
            >
              <ChevronLeft size={18} />
            </button>

            <div className="dm__filter-pickers drs__picker-cell drs__date-picker-cell">
              <DatePicker
                label="Dátum"
                value={dateStr}
                // "Vymazať" posiela null -- vodic bez dna nema co zobrazit a
                // toISODate(null) zhodil celu stranku. Ostava predosly den,
                // rovnako ako v dashboarde dispecera.
                onChange={d => setSelectedDate((current) => (
                  d instanceof Date && !Number.isNaN(d.getTime()) ? d : current
                ))}
              />
            </div>

            <button
              className="dm__week-nav"
              onClick={() =>
                setSelectedDate((d) => {
                  const n = new Date(d);
                  n.setDate(n.getDate() + 1);
                  return n;
                })
              }
              title="Nasledujúci deň"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="dm__filter-pickers drs__picker-cell drs__route-picker">
            {loadingRoutes ? (
              <div className="drs__route-loading">
                <Loader2 size={16} className="drs__spinner" />
              </div>
            ) : (
              <Select
                label="Trasa"
                options={routeOptions}
                value={selectedRouteId}
                onChange={setSelectedRouteId}
                placeholder="Vyber trasu…"
              />
            )}
          </div>
        </div>

        <div className="drs__filter-stats">
          {/* Karta 1: Progres */}
          <div className="drs__stat-card drs__stat-card--mini">
            <span className="drs__mini-value">{deliveredCount} / {allCount}</span>
            <span className="drs__mini-label">Doručené</span>
          </div>

          {/* Karta 2: Hotovosť */}
          <div className="drs__stat-card drs__stat-card--mini">
            <span className="drs__mini-value drs__mini-value--green">{formatCurrency(collectedCash)}</span>
            <span className="drs__mini-label">Vybraté z {formatCurrency(totalCash)}</span>
          </div>

          {/* Karta 3: Aktuálne naložené jedlá */}
          <StatCard
            label="Aktuálne naložené jedlá"
            value={cargoValue}
            className="drs__stat-card--cargo drs__stat-card--full-width drs__stat-card--clickable"
            onClick={() => setCargoModalOpen(true)}
          />
        </div>
      </div>

      {/* ── Modal naložené jedlá ──────────────────────────────────────────── */}
      <Modal
        isOpen={cargoModalOpen}
        onClose={() => setCargoModalOpen(false)}
        title="Aktuálne naložené jedlá"
      >
        <div className="order-items-modal">
          {cargoItems.length === 0 ? (
            <p className="drs__empty">Žiadne jedlá v aktuálnom závoze.</p>
          ) : (
            cargoItems.map(({ code, qty, name }) => (
              <div key={code} className="order-items-modal__row">
                <span className="order-items-modal__qty">{qty}x</span>
                <strong className="order-items-modal__code">{code}</strong>
                <span className="order-items-modal__name">{name}</span>
              </div>
            ))
          )}
        </div>
      </Modal>

      {/* ── Zoznam objednávok ─────────────────────────────────────────────── */}
      <div className="drs__orders">        {!isOwnRoute && selectedRouteId && (
          <div className="drs__readonly-banner">
            <AlertTriangle size={18} className="drs__readonly-banner-icon" />
            <span>Režim prezerania – prezeráte cudziu trasu, zmeny nie sú povolené.</span>
          </div>
        )}        {loadingOrders ? (
          <div className="drs__loading">
            <Loader2 size={22} className="drs__spinner" />
            Načítavam objednávky…
          </div>
        ) : orders.length === 0 ? (
          <div className="drs__empty">
            {selectedRouteId
              ? "Pre zvolenú trasu a dátum nie sú žiadne objednávky."
              : "Nie si priradený k žiadnej trase."}
          </div>
        ) : (
          sortOrders(orders)
            .filter((o) => o.status !== "CANCELLED" || o.payment_type === "NOT_DELIVERED")
            .map((order) => (
              <DriverOrderCard
                key={order.id}
                order={order}
                onDeliveryClick={() => setPaymentModalOrder(order)}
                isOwnRoute={isOwnRoute}
              />
            ))
        )}
      </div>
      {isOwnRoute && (
        <DriverPaymentModal
          isOpen={!!paymentModalOrder}
          onClose={() => setPaymentModalOrder(null)}
          order={paymentModalOrder}
          onSuccess={() => fetchOrders()}
        />
      )}
    </div>
  );
}

export default DriverRoutesSection;
